import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, BookOpen, Shield, Leaf, FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import WholesaleForm from "@/components/WholesaleForm";
import SEO from "@/components/SEO";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = "https://basket-10.wbbasket.ru/vol1591/part159107/159107097/images/big/4.webp";

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const features = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Методика Монтессори",
      description: "Книги на уровне глаз ребенка стимулируют самостоятельность и интерес к чтению",
      color: "bg-[#ADD8E6]",
    },
    {
      icon: <Leaf className="w-6 h-6" />,
      title: "Экологичность",
      description: "Используем только березовую фанеру высшего сорта без вредных покрытий",
      color: "bg-[#98FF98]",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Безопасность",
      description: "Скругленные края, надежное крепление к стене, сертифицированные материалы",
      color: "bg-[#E6E6FA]",
    },
  ];

  return (
    <div data-testid="home-page">
      <SEO
        title="BE KIDS — Детские настенные полки для книг по методике Монтессори | Купить"
        description="Детские книжные полки BE KIDS из березовой фанеры высшего сорта. Книги на уровне глаз ребёнка по методике Монтессори. Размеры L, XL, XXL. Доставка по России. Оптовые заказы."
        path="/"
      />
      {/* Hero Section */}
      <section className="section-padding" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {/* Main Hero Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="md:col-span-7 card-soft p-8 md:p-12 flex flex-col justify-center"
            >
              <span className="label-text text-[#ADD8E6] mb-4" data-testid="hero-label">
                Умная мебель для умных детей
              </span>
              <h1 className="heading-1 mb-6" data-testid="hero-title">
                Детские полки для книг по методике Монтессори
              </h1>
              <p className="body-text mb-8 max-w-xl" data-testid="hero-description">
                BE KIDS создает экологичную детскую мебель из березовой фанеры высшего сорта. 
                Наши настенные полки помогают развивать самостоятельность ребенка — книги 
                располагаются обложками вперед на уровне глаз малыша.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#products">
                  <Button className="btn-primary" data-testid="hero-cta-products">
                    Смотреть товары
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Link to="/instructions">
                  <Button className="btn-secondary" data-testid="hero-cta-instructions">
                    <FileText className="w-4 h-4 mr-2" />
                    Инструкция
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="md:col-span-5"
            >
              <div className="relative h-full min-h-[300px] md:min-h-[500px] rounded-[2rem] overflow-hidden">
                <img
                  src={HERO_IMAGE}
                  alt="Детская комната с полкой BE KIDS"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/20 to-transparent" />
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="md:col-span-6 card-soft p-6 bg-[#FFDAB9]/30"
            >
              <Link to="/claims" className="flex items-center gap-4 group" data-testid="hero-claims-link">
                <div className="w-12 h-12 rounded-full bg-[#FFDAB9] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <AlertCircle className="w-6 h-6 text-[#1A1A1A]" />
                </div>
                <div>
                  <h3 className="heading-3 text-lg group-hover:text-[#808080] transition-colors">
                    Проблемы с товаром?
                  </h3>
                  <p className="text-sm text-[#4A4A4A]">Оформите обращение по рекламации</p>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto text-[#808080] group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="md:col-span-6 card-soft p-6 bg-[#E6E6FA]/30"
            >
              <Link to="/instructions" className="flex items-center gap-4 group" data-testid="hero-instructions-link">
                <div className="w-12 h-12 rounded-full bg-[#E6E6FA] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-[#1A1A1A]" />
                </div>
                <div>
                  <h3 className="heading-3 text-lg group-hover:text-[#808080] transition-colors">
                    Нужна инструкция?
                  </h3>
                  <p className="text-sm text-[#4A4A4A]">Скачайте руководство по сборке</p>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto text-[#808080] group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="label-text text-[#808080] mb-2 block">Почему BE KIDS</span>
            <h2 className="heading-2" data-testid="features-title">Преимущества нашей мебели</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-soft p-8 text-center"
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-16 h-16 ${feature.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                  {feature.icon}
                </div>
                <h3 className="heading-3 mb-3">{feature.title}</h3>
                <p className="body-text text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="seo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-2 mb-8 text-center" data-testid="seo-title">
                О бренде BE KIDS
              </h2>
              <div className="body-text space-y-6" data-testid="seo-content">
                <p>
                  BE KIDS — российский производитель умной детской мебели, специализирующийся на создании 
                  настенных полок для книг по методике Монтессори. Наша миссия — помочь родителям создать 
                  идеальное пространство для развития ребенка, где каждый предмет мебели способствует 
                  формированию самостоятельности, любознательности и любви к чтению с самого раннего возраста.
                </p>
                <p>
                  Все изделия BE KIDS изготавливаются из березовой фанеры высшего сорта — экологически чистого 
                  материала, безопасного для детей. Мы не используем токсичные лаки и краски, сохраняя 
                  натуральную красоту дерева. Каждая полка проходит тщательный контроль качества: проверяется 
                  гладкость поверхностей, надежность соединений и соответствие размерам.
                </p>
                <p>
                  Методика Монтессори, лежащая в основе дизайна наших полок, предполагает размещение книг 
                  обложками вперед на уровне глаз ребенка. Это позволяет малышу самостоятельно выбирать книги 
                  для чтения, развивает визуальное восприятие и формирует привычку к порядку. Настенные 
                  полки BE KIDS экономят пространство детской комнаты и создают уютную атмосферу для обучения.
                </p>
                <p>
                  Продукция BE KIDS представлена на крупнейших российских маркетплейсах — Wildberries и Ozon. 
                  Мы предлагаем три размера настенных полок: L (75 см, 3 полки), XL (110 см, 4 полки) и 
                  XXL (139 см, 5 полок), чтобы каждая семья могла выбрать оптимальный вариант для своего 
                  интерьера. Для оптовых покупателей и партнеров действуют специальные условия сотрудничества.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="section-padding" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="label-text text-[#808080] mb-2 block">Каталог</span>
            <h2 className="heading-2" data-testid="products-title">Наши товары</h2>
            <p className="body-text mt-4 max-w-2xl mx-auto">
              Выберите подходящий размер полки для вашей детской комнаты
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card-soft p-8 animate-pulse">
                  <div className="h-64 bg-[#E6E6FA]/50 rounded-2xl mb-6" />
                  <div className="h-6 bg-[#E6E6FA]/50 rounded mb-4 w-3/4" />
                  <div className="h-4 bg-[#E6E6FA]/50 rounded mb-2" />
                  <div className="h-4 bg-[#E6E6FA]/50 rounded w-5/6" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="products-grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Wholesale Section */}
      <section className="section-padding bg-white" data-testid="wholesale-section-wrapper">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <WholesaleForm />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
